"use client"

import { Link } from "react-router-dom"
import { LandmarkIcon } from "lucide-react"
import type {
  BranchFinanceBoardAccount,
  BranchFinanceBoardBreakdown,
  BranchFinanceBoardMargin,
  FinanceHomeFigures,
} from "@ibada/shared-types/overview"

import { HomeTile, HomeTileEmpty, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"
import { MirroredWeeklyChart } from "@/features/dashboard/home-d4/components/mirrored-weekly-chart"
import {
  EXPENSE_BAR_CLASS,
  EXPENSE_TEXT_CLASS,
  FALLBACK_MUTED_CLASS,
  INCOME_BAR_CLASS,
  INCOME_TEXT_CLASS,
  IncomeExpenseLegend,
  TrackBar,
  type TileFallback,
} from "@/features/finance/overview/church-f3/components/church-tile-parts"
import { dayShortLabel, isNegativeMoney, monthShortLabel } from "@/features/finance/overview/church-f3/lib/church-f3-format"
import {
  TopSourceComparison,
  biggestChange,
  breakdownBars,
  capitalize,
  formatWholeAmount,
  keptBars,
  topSource,
} from "@/features/finance/overview/branch-f1/lib/branch-f1-format"
import type { useBoardWording } from "@/features/finance/overview/branch-f1/hooks/use-board-wording"
import { BranchAccountReconciliationState } from "@ibada/shared-types/overview"
import { richSentence } from "@/features/finance/overview/board/rich-sentence"
import { useLocale, useTranslations } from "@/i18n"
import { formatCurrency } from "@/shared/lib/formatting/currency"
import { cn } from "@ibada/ui"

type Wording = ReturnType<typeof useBoardWording>

/** How many accounts the treasury lists before "and N more". */
const TREASURY_ACCOUNTS = 3

const isZero = (value: string) => Number(value) === 0

/** The period line of the treasury: what came in, went out and went to the parish. */
export type TreasuryPeriod = Pick<FinanceHomeFigures, "income" | "expenses" | "remittancesToParish" | "netChange">

/**
 * Treasury (F1, two tracks): the branch's cash today with how the period moved it, each account with its
 * reconciliation state, nine weeks of mirrored income and expense bars, and the period's money in one line.
 */
export function BranchTreasuryTile({
  figures,
  period,
  accounts,
  currency,
  wording,
  link,
  record,
}: {
  /** Today's cash and the weekly series: current state, never a fallback. */
  figures: FinanceHomeFigures
  /** The period's money: the board's, or the previous period's under a fallback. */
  period: TreasuryPeriod
  /** Null while the board's own figures load or when they failed: the tile still shows the cash. */
  accounts: BranchFinanceBoardAccount[] | null
  currency: string
  wording: Wording
  link: HomeTileLink | null
  record: HomeTileLink | null
}) {
  const t = useTranslations("finance.overview")
  const locale = useLocale()
  const negative = isNegativeMoney(period.netChange)
  const chartEmpty = figures.weekly.every((week) => isZero(week.income) && isZero(week.expense))
  const listed = accounts?.slice(0, TREASURY_ACCOUNTS) ?? []
  const more = (accounts?.length ?? 0) - listed.length

  return (
    <section
      data-testid='branch-tile-treasury'
      className='flex h-full min-h-0 flex-col gap-5 overflow-hidden rounded-2xl border border-border/60 bg-card px-5 py-4 md:flex-row md:gap-6'
    >
      <div className='flex min-w-0 flex-col gap-1.5 md:w-[17.5rem] md:shrink-0'>
        <span className='flex items-start gap-2'>
          <span className='flex min-w-0 flex-col gap-px'>
            <h2 className='truncate text-sm font-bold text-foreground'>{t("widgets.branchTreasury.title")}</h2>
            <span className='truncate text-xs text-muted-foreground'>
              {t(figures.accountCount === 1 ? "branch.treasury.accountsTodayOne" : "branch.treasury.accountsTodayOther", { count: figures.accountCount })}
            </span>
          </span>
          {link ? (
            <Link to={link.href} className='ml-auto shrink-0 text-[12.5px] font-bold text-primary hover:underline'>
              {link.label}
            </Link>
          ) : null}
        </span>
        <span
          data-testid='branch-treasury-cash'
          className='mt-1 text-[2.25rem] leading-none font-extrabold tracking-tight whitespace-nowrap text-foreground tabular-nums'
        >
          {formatCurrency(figures.cashOnHand, currency)}
        </span>
        <span className='flex'>
          <span
            data-testid='branch-treasury-change'
            className={cn(
              "rounded-full px-2.5 py-0.5 text-xs font-bold whitespace-nowrap tabular-nums",
              negative
                ? "bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300"
                : "bg-teal-50 text-teal-700 dark:bg-teal-950/50 dark:text-teal-300",
            )}
          >
            {t("branch.treasury.change", {
              amount: `${negative ? "" : "+"}${formatCurrency(period.netChange, currency)}`,
              during: wording.during,
            })}
          </span>
        </span>
        <ul className='mt-1.5 flex flex-col divide-y divide-border/60 border-t border-border/60'>
          {listed.map((account) => (
            <li key={account.accountId} data-testid='branch-treasury-account' className='flex items-center gap-2.5 py-1.5'>
              <span className='inline-flex size-7 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground'>
                <LandmarkIcon aria-hidden className='size-3.5' />
              </span>
              <span className='flex min-w-0 flex-col'>
                <span className='truncate text-[13px] font-bold text-foreground'>{account.name}</span>
                <span className='truncate text-[11.5px] text-muted-foreground'>
                  {account.reconciliation === BranchAccountReconciliationState.Never || !account.lastReconciledAt
                    ? t("branch.treasury.toReconcile")
                    : t(
                        account.reconciliation === BranchAccountReconciliationState.Overdue
                          ? "branch.treasury.reconciledLate"
                          : "branch.treasury.reconciledOn",
                        { date: dayShortLabel(account.lastReconciledAt, locale) },
                      )}
                </span>
              </span>
              <span className='ml-auto shrink-0 text-[13.5px] font-bold tabular-nums'>
                {formatCurrency(account.balance, currency)}
              </span>
            </li>
          ))}
        </ul>
        {more > 0 ? <span className='text-[11.5px] text-muted-foreground'>{t(more === 1 ? "branch.treasury.moreAccountsOne" : "branch.treasury.moreAccountsOther", { count: more })}</span> : null}
      </div>

      <div className='flex min-h-0 min-w-0 flex-1 flex-col gap-1.5'>
        <div className='flex flex-wrap items-center gap-3'>
          <span className='text-[12.5px] font-bold text-muted-foreground'>
            {t("branch.treasury.weeks", { count: figures.weekly.length })}
          </span>
          <span className='ml-auto'>
            <IncomeExpenseLegend income={t("church.branches.legendIncome")} expense={t("branch.treasury.legendOut")} />
          </span>
        </div>
        {chartEmpty ? (
          <HomeTileEmpty message={t("branch.treasury.chartEmpty")} action={record} />
        ) : (
          <MirroredWeeklyChart weeks={figures.weekly} currency={currency} />
        )}
        <p data-testid='branch-treasury-period' className='flex flex-wrap gap-x-4 gap-y-0.5 text-[13px] text-muted-foreground'>
          <span>
            {richSentence(
              t,
              "branch.treasury.periodLine",
              { during: capitalize(wording.during, locale) },
              {
                income: { text: `+${formatCurrency(period.income, currency)}`, className: INCOME_TEXT_CLASS },
                spent: { text: `−${formatCurrency(period.expenses, currency)}`, className: EXPENSE_TEXT_CLASS },
                remitted: { text: `−${formatCurrency(period.remittancesToParish, currency)}` },
              },
            )}
          </span>
        </p>
      </div>
    </section>
  )
}


/** One labelled bar of a breakdown: the name and amount above, the bar beneath. */
function BreakdownBarRow({ name, amount, ratio, barClass, currency }: { name: string; amount: string; ratio: number; barClass: string; currency: string }) {
  return (
    <li data-testid='branch-breakdown-row' className='flex flex-col gap-1'>
      <span className='flex items-baseline gap-2 text-[12.5px]'>
        <span className='truncate font-semibold text-foreground'>{name}</span>
        <span className='ml-auto shrink-0 font-bold tabular-nums'>{formatCurrency(amount, currency)}</span>
      </span>
      <TrackBar ratio={ratio} className={barClass} />
    </li>
  )
}

/** Where the money comes from (F1): income by account, and the top source's share against the period before. */
export function BranchIncomeTile({
  income,
  currency,
  wording,
  fallback,
  link,
}: {
  income: BranchFinanceBoardBreakdown
  currency: string
  wording: Wording
  fallback: TileFallback | null
  link: HomeTileLink | null
}) {
  const t = useTranslations("finance.overview")
  const bars = breakdownBars(income)
  const top = topSource(income)
  return (
    <HomeTile
      title={t("widgets.branchIncome.title")}
      subtitle={fallback?.label ?? t("branch.income.subtitle", { during: wording.during })}
      link={link}
      testId='branch-tile-income'
    >
      <ul data-fallback={fallback ? true : undefined} className={cn("flex flex-col gap-2.5", fallback && FALLBACK_MUTED_CLASS)}>
        {bars.map((bar) => (
          <BreakdownBarRow key={bar.id} name={bar.name} amount={bar.amount} ratio={bar.ratio} barClass={INCOME_BAR_CLASS} currency={currency} />
        ))}
      </ul>
      {top ? (
        <p data-testid='branch-income-sentence' className='mt-auto text-xs text-muted-foreground'>
          {richSentence(
            t,
            top.comparison === TopSourceComparison.None ? "branch.income.sentence" : "branch.income.sentenceCompared",
            {
              name: top.name,
              comparison:
                top.comparison === TopSourceComparison.Same || top.previousPercent === null
                  ? wording.same()
                  : wording.against(top.previousPercent),
            },
            { percent: { text: t("church.share.percent", { value: top.percent }) } },
          )}
        </p>
      ) : null}
    </HomeTile>
  )
}

/** Where the money goes (F1): expenses by line, and the line that moved the most against the period before. */
export function BranchExpensesTile({
  expenses,
  currency,
  wording,
  fallback,
  link,
}: {
  expenses: BranchFinanceBoardBreakdown
  currency: string
  wording: Wording
  fallback: TileFallback | null
  link: HomeTileLink | null
}) {
  const t = useTranslations("finance.overview")
  const bars = breakdownBars(expenses)
  const change = biggestChange(expenses)
  return (
    <HomeTile
      title={t("widgets.branchExpenses.title")}
      subtitle={fallback?.label ?? t("branch.expenses.subtitle", { during: wording.during })}
      link={link}
      testId='branch-tile-expenses'
    >
      <ul data-fallback={fallback ? true : undefined} className={cn("flex flex-col gap-2.5", fallback && FALLBACK_MUTED_CLASS)}>
        {bars.map((bar) => (
          <BreakdownBarRow key={bar.id} name={bar.name} amount={bar.amount} ratio={bar.ratio} barClass={EXPENSE_BAR_CLASS} currency={currency} />
        ))}
      </ul>
      {change ? (
        <p data-testid='branch-expenses-sentence' className='mt-auto text-xs text-muted-foreground'>
          {richSentence(
            t,
            "branch.expenses.change",
            { name: change.name, comparison: wording.versus() },
            {
              change: {
                text: `${change.percent > 0 ? "+" : "−"}${t("church.share.percent", { value: Math.abs(change.percent) })}`,
                className: change.percent > 0 ? EXPENSE_TEXT_CLASS : INCOME_TEXT_CLASS,
              },
            },
          )}
        </p>
      ) : null}
    </HomeTile>
  )
}

/**
 * The month's margin (F1): of every 100 the branch receives, what it spent and what it keeps, as a sentence and a
 * split bar, and the kept share of the last six months. Ratios of the branch's own income; the "100" is a unit of
 * the sentence, never a recorded amount.
 */
export function BranchMarginTile({
  margin,
  currency,
  wording,
  fallback,
}: {
  margin: BranchFinanceBoardMargin
  currency: string
  wording: Wording
  fallback: TileFallback | null
}) {
  const t = useTranslations("finance.overview")
  const locale = useLocale()
  const spent = margin.spentPer100 ?? 0
  const kept = margin.keptPer100 ?? 0
  const overspent = kept < 0
  const bars = keptBars(margin.months)
  const amount = (value: number) => formatWholeAmount(value, currency)

  return (
    <HomeTile
      title={t("widgets.branchMargin.title")}
      subtitle={fallback?.label ?? t("branch.margin.subtitle", { during: wording.during })}
      testId='branch-tile-margin'
    >
      <p data-testid='branch-margin-sentence' className='text-[14.5px] leading-snug text-foreground'>
        {richSentence(
          t,
          overspent ? "branch.margin.overspent" : "branch.margin.sentence",
          {},
          {
            hundred: { text: amount(100) },
            spent: { text: amount(spent), className: EXPENSE_TEXT_CLASS },
            kept: { text: amount(Math.abs(kept)), className: overspent ? EXPENSE_TEXT_CLASS : INCOME_TEXT_CLASS },
          },
        )}
      </p>
      <div aria-hidden data-fallback={fallback ? true : undefined} className={cn("flex h-3.5 gap-0.5", fallback && FALLBACK_MUTED_CLASS)}>
        <span className={cn("rounded-[4px]", EXPENSE_BAR_CLASS)} style={{ width: `${Math.min(100, Math.max(0, spent))}%` }} />
        {kept > 0 ? <span className={cn("rounded-[4px]", INCOME_BAR_CLASS)} style={{ width: `${kept}%` }} /> : null}
      </div>
      <span className='flex text-xs text-muted-foreground'>
        <span>{t("branch.margin.spentShare", { value: spent })}</span>
        <span className='ml-auto'>{t("branch.margin.keptShare", { value: kept })}</span>
      </span>
      <span className='mt-auto text-xs font-bold text-muted-foreground'>{t("branch.margin.history", { count: bars.length })}</span>
      <div data-testid='branch-margin-history' className='flex h-14 items-end gap-2'>
        {bars.map((bar, index) => {
          const latest = index === bars.length - 1
          return (
            <span key={bar.month} className='flex h-full flex-1 flex-col items-center justify-end gap-0.5'>
              <span className={cn("text-[10.5px] font-bold tabular-nums", latest ? "text-foreground" : "text-muted-foreground")}>
                {bar.kept === null ? "–" : bar.kept}
              </span>
              <span
                className={cn("w-full rounded-t-[4px]", INCOME_BAR_CLASS, !latest && "opacity-45")}
                style={{ height: `${Math.max(bar.ratio > 0 ? 3 : 0, bar.ratio * 36)}px` }}
              />
            </span>
          )
        })}
      </div>
      <div className='flex gap-2 text-[10.5px] text-muted-foreground'>
        {bars.map((bar) => (
          <span key={bar.month} className='flex-1 text-center'>
            {monthShortLabel(bar.month, locale)}
          </span>
        ))}
      </div>
    </HomeTile>
  )
}
